import * as React from 'react'
import * as RechartsPrimitive from 'recharts'
import { cn, isRecord } from '@/lib/utils'
import type { CSSVariableStyle } from '@/lib/css-variables'

const THEMES = {
  light: '',
  dark: '.dark',
}

type ChartTheme = keyof typeof THEMES

const INITIAL_DIMENSION = {
  width: 320,
  height: 200,
}

export type ChartConfig = Record<
  string,
  {
    label?: React.ReactNode
    icon?: React.ComponentType
  } & ({ color?: string; theme?: never } | { color?: never; theme: Record<ChartTheme, string> })
>

type ChartContextValue = {
  config: ChartConfig
}

type ChartTooltipPayloadItem = RechartsPrimitive.TooltipPayloadEntry
type ChartLegendPayloadItem = RechartsPrimitive.LegendPayload

// React's CSSProperties has no index signature for custom properties, so inline styles that set
// CSS variables are checked against this wider shape.

const ChartContext = React.createContext<ChartContextValue | null>(null)

function isChartTheme(value: string): value is ChartTheme {
  return value in THEMES
}

function useChart() {
  const context = React.useContext(ChartContext)

  if (!context) {
    throw new Error('useChart must be used within a <ChartContainer />')
  }

  return context
}

export type ChartContainerProps = React.ComponentProps<'div'> & {
  config: ChartConfig
  children: React.ComponentProps<typeof RechartsPrimitive.ResponsiveContainer>['children']
  initialDimension?: React.ComponentProps<
    typeof RechartsPrimitive.ResponsiveContainer
  >['initialDimension']
}

function ChartContainer({
  id,
  className,
  children,
  config,
  initialDimension = INITIAL_DIMENSION,
  ...props
}: ChartContainerProps) {
  const uniqueId = React.useId()
  const chartId = `chart-${id ?? uniqueId.replace(/:/g, '')}`

  return (
    <ChartContext.Provider value={{ config }}>
      <div
        data-slot="chart"
        data-chart={chartId}
        className={cn(
          "flex aspect-video justify-center text-xs [&_.recharts-cartesian-axis-tick_text]:fill-muted-foreground [&_.recharts-cartesian-grid_line[stroke='#ccc']]:stroke-border/50 [&_.recharts-curve.recharts-tooltip-cursor]:stroke-border [&_.recharts-dot[stroke='#fff']]:stroke-transparent [&_.recharts-layer]:outline-hidden [&_.recharts-polar-grid_[stroke='#ccc']]:stroke-border [&_.recharts-radial-bar-background-sector]:fill-muted [&_.recharts-rectangle.recharts-tooltip-cursor]:fill-muted [&_.recharts-reference-line_[stroke='#ccc']]:stroke-border [&_.recharts-sector]:outline-hidden [&_.recharts-sector[stroke='#fff']]:stroke-transparent [&_.recharts-surface]:outline-hidden",
          '[&_[tabindex]:focus:not(:focus-visible)]:outline-none [&_[tabindex]:focus-visible]:outline-2 [&_[tabindex]:focus-visible]:outline-solid [&_[tabindex]:focus-visible]:outline-ring',
          className
        )}
        {...props}
      >
        <ChartStyle id={chartId} config={config} />
        <RechartsPrimitive.ResponsiveContainer initialDimension={initialDimension}>
          {children}
        </RechartsPrimitive.ResponsiveContainer>
      </div>
    </ChartContext.Provider>
  )
}

export type ChartStyleProps = {
  id: string
  config: ChartConfig
}

function ChartStyle({ id, config }: ChartStyleProps) {
  const colorConfig = Object.entries(config).filter(
    ([, itemConfig]) => itemConfig.theme ?? itemConfig.color
  )

  if (!colorConfig.length) {
    return null
  }

  return (
    <style
      dangerouslySetInnerHTML={{
        __html: Object.entries(THEMES)
          .map(
            ([theme, prefix]) => `
${prefix} [data-chart=${id}] {
${colorConfig
  .map(([key, itemConfig]) => {
    // THEMES keys are the only themes, so the guard always passes; it narrows the
    // Object.entries string key for the typed theme lookup.
    const color = (isChartTheme(theme) ? itemConfig.theme?.[theme] : undefined) ?? itemConfig.color

    return color ? `  --color-${key}: ${color};` : null
  })
  .join('\n')}
}
`
          )
          .join('\n'),
      }}
    />
  )
}

const ChartTooltip = RechartsPrimitive.Tooltip

/**
 * Custom row renderer. Unlike Recharts' own formatter, the fifth argument is the hovered item's
 * data row (`item.payload`), not the whole tooltip payload array.
 */
export type ChartTooltipFormatter = (
  value: RechartsPrimitive.TooltipValueType,
  name: NonNullable<ChartTooltipPayloadItem['name']>,
  item: ChartTooltipPayloadItem,
  index: number,
  row: unknown
) => React.ReactNode

/**
 * Pass as `<ChartTooltip content={<ChartTooltipContent />} />`; Recharts injects `active`,
 * `payload`, and `label` when it clones the element.
 */
export type ChartTooltipContentProps = {
  active?: boolean
  payload?: ReadonlyArray<ChartTooltipPayloadItem>
  label?: React.ReactNode
  className?: string
  indicator?: 'line' | 'dot' | 'dashed'
  hideLabel?: boolean
  hideIndicator?: boolean
  labelFormatter?: (
    label: React.ReactNode,
    payload: ReadonlyArray<ChartTooltipPayloadItem>
  ) => React.ReactNode
  labelClassName?: string
  formatter?: ChartTooltipFormatter
  color?: string
  nameKey?: string
  labelKey?: string
}

function ChartTooltipContent({
  active,
  payload,
  className,
  indicator = 'dot',
  hideLabel = false,
  hideIndicator = false,
  label,
  labelFormatter,
  labelClassName,
  formatter,
  color,
  nameKey,
  labelKey,
}: ChartTooltipContentProps) {
  const { config } = useChart()

  const tooltipLabel = React.useMemo(() => {
    if (hideLabel || !payload?.length) {
      return null
    }

    const [item] = payload
    const key = String(labelKey ?? item?.dataKey ?? item?.name ?? 'value')
    const itemConfig = getPayloadConfigFromPayload(config, item, key)
    const value =
      !labelKey && typeof label === 'string' ? (config[label]?.label ?? label) : itemConfig?.label

    if (labelFormatter) {
      return (
        <div className={cn('font-medium', labelClassName)}>{labelFormatter(value, payload)}</div>
      )
    }

    if (!value) {
      return null
    }

    return <div className={cn('font-medium', labelClassName)}>{value}</div>
  }, [label, labelFormatter, payload, hideLabel, labelClassName, config, labelKey])

  if (!active || !payload?.length) {
    return null
  }

  const nestLabel = payload.length === 1 && indicator !== 'dot'

  return (
    <div
      className={cn(
        'grid min-w-[8rem] items-start gap-1.5 rounded-md border border-border/50 bg-background px-2.5 py-1.5 text-xs shadow-xl',
        className
      )}
    >
      {!nestLabel ? tooltipLabel : null}
      <div className="grid gap-1.5">
        {payload
          .filter((item) => item.type !== 'none')
          .map((item, index) => {
            const key = String(nameKey ?? item.name ?? item.dataKey ?? 'value')
            const itemConfig = getPayloadConfigFromPayload(config, item, key)
            // Recharts types the data row as `any`; read it as unknown and narrow.
            const row: unknown = item.payload
            const rowFill = isRecord(row) && typeof row.fill === 'string' ? row.fill : undefined
            const indicatorColor = color ?? rowFill ?? item.color
            const indicatorStyle: CSSVariableStyle = {
              '--color-bg': indicatorColor,
              '--color-border': indicatorColor,
            }

            return (
              <div
                key={index}
                className={cn(
                  'flex w-full flex-wrap items-stretch gap-2 [&>svg]:h-2.5 [&>svg]:w-2.5 [&>svg]:text-muted-foreground',
                  indicator === 'dot' && 'items-center'
                )}
              >
                {formatter && item?.value !== undefined && item.name ? (
                  formatter(item.value, item.name, item, index, row)
                ) : (
                  <>
                    {itemConfig?.icon ? (
                      <itemConfig.icon />
                    ) : (
                      !hideIndicator && (
                        <div
                          className={cn(
                            'shrink-0 rounded-[2px] border-(--color-border) bg-(--color-bg)',
                            {
                              'h-2.5 w-2.5': indicator === 'dot',
                              'w-1': indicator === 'line',
                              'w-0 border-[1.5px] border-dashed bg-transparent':
                                indicator === 'dashed',
                              'my-0.5': nestLabel && indicator === 'dashed',
                            }
                          )}
                          style={indicatorStyle}
                        />
                      )
                    )}
                    <div
                      className={cn(
                        'flex flex-1 justify-between leading-none',
                        nestLabel ? 'items-end' : 'items-center'
                      )}
                    >
                      <div className="grid gap-1.5">
                        {nestLabel ? tooltipLabel : null}
                        <span className="text-muted-foreground">
                          {itemConfig?.label ?? item.name}
                        </span>
                      </div>
                      {item.value != null && (
                        <span className="font-medium tabular-nums text-foreground">
                          {typeof item.value === 'number'
                            ? item.value.toLocaleString()
                            : String(item.value)}
                        </span>
                      )}
                    </div>
                  </>
                )}
              </div>
            )
          })}
      </div>
    </div>
  )
}

const ChartLegend = RechartsPrimitive.Legend

/**
 * Pass as `<ChartLegend content={<ChartLegendContent />} />`; Recharts injects `payload` and
 * `verticalAlign` when it clones the element.
 */
export type ChartLegendContentProps = {
  className?: string
  hideIcon?: boolean
  payload?: ReadonlyArray<ChartLegendPayloadItem>
  verticalAlign?: RechartsPrimitive.DefaultLegendContentProps['verticalAlign']
  nameKey?: string
}

function ChartLegendContent({
  className,
  hideIcon = false,
  payload,
  verticalAlign = 'bottom',
  nameKey,
}: ChartLegendContentProps) {
  const { config } = useChart()

  if (!payload?.length) {
    return null
  }

  return (
    <div
      className={cn(
        'flex items-center justify-center gap-4',
        verticalAlign === 'top' ? 'pb-3' : 'pt-3',
        className
      )}
    >
      {payload
        .filter((item) => item.type !== 'none')
        .map((item, index) => {
          const key = String(nameKey ?? item.dataKey ?? 'value')
          const itemConfig = getPayloadConfigFromPayload(config, item, key)

          return (
            <div
              key={index}
              className="flex items-center gap-1.5 [&>svg]:h-3 [&>svg]:w-3 [&>svg]:text-muted-foreground"
            >
              {itemConfig?.icon && !hideIcon ? (
                <itemConfig.icon />
              ) : (
                <div
                  className="h-2 w-2 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: item.color }}
                />
              )}
              {itemConfig?.label}
            </div>
          )
        })}
    </div>
  )
}

function getPayloadConfigFromPayload(config: ChartConfig, payload: unknown, key: string) {
  if (!isRecord(payload)) {
    return undefined
  }

  const nestedPayload =
    'payload' in payload && isRecord(payload.payload) ? payload.payload : undefined

  let configLabelKey = key
  const payloadLabelKey = key in payload ? payload[key] : undefined
  const nestedPayloadLabelKey =
    nestedPayload && key in nestedPayload ? nestedPayload[key] : undefined

  if (typeof payloadLabelKey === 'string') {
    configLabelKey = payloadLabelKey
  } else if (typeof nestedPayloadLabelKey === 'string') {
    configLabelKey = nestedPayloadLabelKey
  }

  return configLabelKey in config ? config[configLabelKey] : config[key]
}

export {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
}
